import { useEffect, useMemo, useRef, useState } from "react";
import { GOOGLE_API_KEY } from "../../../../config";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import { useJsApiLoader } from "@react-google-maps/api";
import { calculateGeographicHeading } from "../../../map/utils/map-utils";

const StreetViewComponentBase = () => {
  const { mapStore, hlaStore } = useStores();
  const streetViewRef = useRef(null);
  const panoramaRef = useRef<any>(null);
  const hlaWay = hlaStore.getSelected()!;
  const [streetViewLoaded, setLoaded] = useState(false);

  const { isLoaded: scriptLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
  });

  const { lat, lng, heading } = useMemo(() => {
    const [lng, lat] = hlaWay.coordinates[0];
    const heading = calculateGeographicHeading(
      hlaWay.coordinates[0],
      hlaWay.coordinates[1],
    );
    return { lng, lat, heading };
  }, [hlaWay]);

  useEffect(() => {
    const listeners: any[] = [];

    const initStreetView = () => {
      if (!streetViewRef.current) return;

      const panorama = new window.google.maps.StreetViewPanorama(
        streetViewRef.current,
        {
          position: { lat, lng },
          pov: { heading: 0, pitch: 0 },
          zoom: 1,
          addressControl: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
          showRoadLabels: true,
        },
      );

      panoramaRef.current = panorama;

      listeners.push(
        panorama.addListener("position_changed", () => {
          const p = panorama.getPosition?.();
          mapStore.streetViewMapSource?.update({
            position: p ? { lat: p.lat(), lng: p.lng() } : undefined,
          });

          if (p) {
            mapStore.map?.flyTo({
              center: [p.lng(), p.lat()],
              essential: true,
              zoom: 18,
            });
          }
        }),
      );

      listeners.push(
        panorama.addListener("pov_changed", () => {
          const currentPov = panorama.getPov?.();

          mapStore.streetViewMapSource?.update({
            pov: {
              pitch: currentPov?.pitch ?? 0,
              heading: currentPov?.heading ?? 0,
            },
          });
        }),
      );

      setLoaded(true);
    };

    if (scriptLoaded) {
      initStreetView();
    }

    return () => {
      listeners.forEach((l) => l && l.remove && l.remove());
      mapStore.streetViewMapSource?.update({
        position: undefined,
      });
    };
    // eslint-disable-next-line
  }, [scriptLoaded]);

  useEffect(() => {
    if (panoramaRef.current && streetViewLoaded) {
      panoramaRef.current.setPosition({ lat, lng });
      panoramaRef.current.setPov({ heading, pitch: 0 });
    }
  }, [lat, lng, heading, streetViewLoaded]);

  return (
    <div
      ref={streetViewRef}
      style={{ width: "100%", height: "100%", minHeight: "400px" }}
    />
  );
};

export const StreetViewComponent = observer(StreetViewComponentBase);
